import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SortableHeader, TablePager } from '@/components/SortableTable';
import { Badge, Card, CardContent, CardHeader } from '@/components/ui';
import { ontologyApi, type OntologyGap } from '@/lib/api';
import { useTableState } from '@/lib/use-table-state';

function buildDraftLink(gap: OntologyGap): string {
  const params = new URLSearchParams({
    gap: gap.gap_id,
    label: gap.entity_label,
    frequency: String(gap.frequency),
  });
  if (gap.candidate_class) params.set('candidate', gap.candidate_class);
  return `/studio/ontology/drafts/new?${params.toString()}`;
}

type GapColumn = 'entityLabel' | 'frequency' | 'candidateClass' | 'confidence' | 'firstSeen';

const GAP_COLUMNS = [
  { key: 'entityLabel' as const, accessor: (g: OntologyGap) => g.entity_label.toLowerCase() },
  { key: 'frequency' as const, accessor: (g: OntologyGap) => g.frequency },
  { key: 'candidateClass' as const, accessor: (g: OntologyGap) => g.candidate_class ?? '' },
  { key: 'confidence' as const, accessor: (g: OntologyGap) => g.candidate_confidence ?? -1 },
  { key: 'firstSeen' as const, accessor: (g: OntologyGap) => g.first_seen },
];

/**
 * Spec S09 — Ontology gap queue. DE Analyst surface for entities the
 * pipeline could not classify against the live ontology.
 */
export function OntologyGapQueue() {
  const { t, i18n } = useTranslation(['studio', 'common']);
  const { data, isLoading } = useQuery({
    queryKey: ['studio', 'ontology', 'gaps'],
    queryFn: () => ontologyApi.listGaps({ status: 'open', limit: 100 }),
  });

  const tableState = useTableState<OntologyGap, GapColumn>({
    rows: data ?? [],
    columns: GAP_COLUMNS,
    initialSort: { by: 'frequency', dir: 'desc' },
  });

  const sortable = (key: GapColumn) => ({
    active: tableState.sort?.by === key,
    direction: tableState.sort?.dir,
    onClick: () => tableState.toggleSort(key),
  });

  return (
    <div className="space-y-4 p-6">
      <header>
        <h1 className="text-display text-brand-900">{t('studio:ontology.gapQueue.title')}</h1>
        <p className="mt-1 text-body text-slate-600">
          {t('studio:ontology.gapQueue.subtitle')}
        </p>
      </header>

      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">
            {data ? t('studio:ontology.gapQueue.openCount', { count: data.length }) : '—'}
          </h2>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <p className="px-4 py-3 text-small text-slate-500">{t('common:status.loading')}</p>
          )}
          {data && data.length === 0 && (
            <p className="px-4 py-6 text-center text-body text-slate-500">
              {t('studio:ontology.gapQueue.noGaps')}
            </p>
          )}
          {data && data.length > 0 && (
            <>
              <table className="w-full text-body">
                <thead className="border-b border-slate-200 bg-slate-50 text-h4 text-slate-600">
                  <tr>
                    <SortableHeader
                      label={t('studio:ontology.gapQueue.columns.entityLabel')}
                      {...sortable('entityLabel')}
                    />
                    <SortableHeader
                      label={t('studio:ontology.gapQueue.columns.frequency')}
                      {...sortable('frequency')}
                    />
                    <SortableHeader
                      label={t('studio:ontology.gapQueue.columns.candidateClass')}
                      {...sortable('candidateClass')}
                    />
                    <SortableHeader
                      label={t('studio:ontology.gapQueue.columns.confidence')}
                      {...sortable('confidence')}
                    />
                    <SortableHeader
                      label={t('studio:ontology.gapQueue.columns.firstSeen')}
                      {...sortable('firstSeen')}
                    />
                    <th className="w-8 px-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tableState.page.map((gap) => (
                    <tr key={gap.gap_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-brand-900">
                        {gap.entity_label}
                      </td>
                      <td className="px-3 py-2 text-small text-slate-700">{gap.frequency}</td>
                      <td className="px-3 py-2 font-mono text-small text-slate-700">
                        {gap.candidate_class ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-small">
                        {gap.candidate_confidence != null && (
                          <Badge
                            variant={
                              gap.candidate_confidence >= 0.75
                                ? 'success'
                                : gap.candidate_confidence >= 0.5
                                  ? 'warning'
                                  : 'neutral'
                            }
                          >
                            {(gap.candidate_confidence * 100).toFixed(0)}%
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-2 text-small text-slate-500">
                        {new Date(gap.first_seen).toLocaleDateString(i18n.language)}
                      </td>
                      <td className="px-2 py-2">
                        <Link
                          to={buildDraftLink(gap)}
                          className="flex items-center gap-1 text-small text-accent-700 hover:text-accent"
                        >
                          <Sparkles className="h-3.5 w-3.5" aria-hidden />
                          {t('studio:ontology.gapQueue.draftFromGap')}
                          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="border-t border-slate-200">
                <TablePager
                  pageIndex={tableState.pageIndex}
                  pageCount={tableState.pageCount}
                  pageSize={tableState.pageSize}
                  total={tableState.total}
                  onPageChange={tableState.setPageIndex}
                  onPageSizeChange={tableState.setPageSize}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
